import { GQLOrderItemPrintDecorationType, GQLOrderItemTileFragment } from '@theprintbar/graphql';
import {
	OrderItemNeedsEmbApproval,
	OrderItemNeedsScrApproval,
	capitalise,
	cn,
	getCurrencyString,
	getProductImageUrl,
} from '@theprintbar/shared';
import { ImageCell, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@theprintbar/ui';

interface LineItemProps {
	items?: GQLOrderItemTileFragment[] | null;
	generateLink?: (id: string) => string;
	webDomain?: string;
	staff?: boolean;
}

export function LineItemTable({ items, webDomain, generateLink, staff }: LineItemProps) {
	const decorationTypes =
		items?.reduce((acc, item) => {
			item.prints?.forEach(print => {
				if (!acc.includes(print.decoration)) {
					acc.push(print.decoration);
				}
			});
			return acc;
		}, [] as GQLOrderItemPrintDecorationType[]) || [];

	const hasAwaitingApproval = items?.some(item => OrderItemNeedsEmbApproval(item) || OrderItemNeedsScrApproval(item));
	const has3pl = items?.some(item => item.sizes?.some(size => !!size.storeSuppliedVariant));
	const hasCancelled = items?.some(item => item.cancelled);

	return (
		<Table>
			<TableHead>
				<TableRow>
					<TableHeader />
					{(hasAwaitingApproval || hasCancelled) && <TableHeader>Status</TableHeader>}
					{has3pl && <TableHeader>3PL</TableHeader>}
					{staff ? (
						<TableHeader>Product</TableHeader>
					) : (
						<>
							<TableHeader>Brand</TableHeader>
							<TableHeader>Product</TableHeader>
						</>
					)}
					<TableHeader>Colour</TableHeader>
					<TableHeader centered>Quantity</TableHeader>
					<TableHeader centered>Sizes</TableHeader>
					<TableHeader centered>Decorations</TableHeader>
					{decorationTypes?.map(decorationType => (
						<TableHeader centered key={decorationType}>
							{decorationType.toUpperCase()}
						</TableHeader>
					))}
					<TableHeader align="right">Total Price</TableHeader>
				</TableRow>
			</TableHead>
			<TableBody>
				{items
					?.sort((a, b) => {
						//Cancelled items should be at the bottom
						if (a.cancelled && !b.cancelled) return 1;
						if (!a.cancelled && b.cancelled) return -1;
						//After that it should be based on id
						if (a.id > b.id) return -1;
						if (a.id < b.id) return 1;
						return 0;
					})
					.map(item => {
						const decorationSides =
							decorationTypes?.map(decorationType => {
								return item.prints?.filter(print => print.decoration === decorationType).map(p => capitalise(p.side)) || [];
							}) || [];

						return (
							<TableRow
								key={item.id}
								href={generateLink?.(item.id) || `./${item.id}`}
								className={cn(
									(OrderItemNeedsEmbApproval(item) || OrderItemNeedsScrApproval(item)) &&
										'text-white bg-orange hover:bg-orange-400',
									item.cancelled && 'text-white bg-red hover:bg-red-400',
								)}
							>
								<ImageCell centered>
									{/* Add a fallback image */}
									<img
										loading="lazy"
										src={item.prints?.[0]?.preview.url || ''}
										className="object-cover w-full h-full"
										onError={e => {
											e.currentTarget.src = getProductImageUrl({
												productCode: item.product?.code || '',
												colourCode: item.colour?.colour.code,
												webDomain,
											});
										}}
									/>
								</ImageCell>
								{item.cancelled ? (
									<TableCell>Cancelled</TableCell>
								) : hasAwaitingApproval ? (
									<TableCell>
										{OrderItemNeedsEmbApproval(item) || OrderItemNeedsScrApproval(item)
											? 'Awaiting Approval'
											: 'Approved'}
									</TableCell>
								) : null}
								{has3pl && (
									<TableCell centered>{item.sizes?.some(size => !!size.storeSuppliedVariant) ? '3PL' : '-'}</TableCell>
								)}
								{staff ? (
									<TableCell>{item.product?.code || 'Not found...'}</TableCell>
								) : (
									<>
										<TableCell>{item.product?.brand.name || 'Not found...'}</TableCell>
										<TableCell>{item.product?.name || 'Not found...'}</TableCell>
									</>
								)}

								<TableCell>{item.colour?.colour?.name}</TableCell>
								<TableCell centered>{item.sizes?.reduce((acc, size) => acc + size.quantity, 0) || 0}</TableCell>
								<TableCell centered>
									{item.sizes
										?.sort((a, b) => (a.size?.order || 1) - (b.size?.order || 1))
										.map(({ size, quantity }, index) => (
											<p className="text-xs" key={size?.id}>
												{quantity} x {size?.code}
											</p>
										))}
								</TableCell>
								<TableCell centered>{item.prints?.length}</TableCell>
								{decorationSides.map((decorationSide, index) => (
									<TableCell centered key={index}>
										{decorationSide.join(', ') || 'None'}
									</TableCell>
								))}
								<TableCell align="right">{getCurrencyString(item.total)}</TableCell>
							</TableRow>
						);
					})}
				{!items?.length && (
					<TableRow>
						<TableCell colSpan={3} className="text-lg text-grey">
							No Items found...
						</TableCell>
					</TableRow>
				)}
			</TableBody>
		</Table>
	);
}
